import { useState, useEffect, useCallback } from 'react';
import { X, Plus, GripVertical, Trash2 } from 'lucide-react';
import { supabase } from '../../lib/supabase';
import { palette, shadow, admin } from '../../styles/tokens';
import { useAuthStore } from '../../stores/authStore';
import { ConfirmDialog } from '../../components/common/ConfirmDialog';

interface Resource {
  id: string;
  name: string;
  type: string;
  sort_order: number;
  active: boolean;
  assigned_user_id: string | null;
  monthly_rent: number | null;
  status: string;
}

interface TeamUser { id: string; first_name: string; last_name: string; employment_type: string }

interface Props {
  isOpen: boolean;
  onClose: () => void;
  onChanged: () => void;
}

export function ChairManagementModal({ isOpen, onClose, onChanged }: Props) {
  const business = useAuthStore(s => s.business);
  const businessId = business?.id;
  const [resources, setResources] = useState<Resource[]>([]);
  const [operators, setOperators] = useState<TeamUser[]>([]);
  const [newName, setNewName] = useState('');
  const [saving, setSaving] = useState(false);
  const [confirmDelete, setConfirmDelete] = useState<Resource | null>(null);
  const [deleting, setDeleting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const fetch = useCallback(async () => {
    if (!businessId) return;
    const [rRes, uRes] = await Promise.all([
      supabase.from('schedule_resources').select('*').eq('business_id', businessId).order('sort_order'),
      supabase.from('users').select('id, first_name, last_name, employment_type').eq('business_id', businessId).eq('status', 'active'),
    ]);
    setResources((rRes.data ?? []) as Resource[]);
    setOperators((uRes.data ?? []) as TeamUser[]);
  }, [businessId]);

  // eslint-disable-next-line react-hooks/set-state-in-effect
  useEffect(() => { if (isOpen) void fetch(); }, [isOpen, fetch]);

  const addChair = async () => {
    if (!businessId || !newName.trim()) return;
    setError(null);
    setSaving(true);
    const { error: insertErr } = await supabase.from('schedule_resources').insert({
      business_id: businessId, name: newName.trim(), type: 'chair',
      sort_order: resources.length, active: true, status: 'available',
    });
    setSaving(false);
    if (insertErr) {
      setError(`Couldn't add chair: ${insertErr.message}`);
      return;
    }
    setNewName('');
    await fetch();
    onChanged();
  };

  const assignOperator = async (resourceId: string, userId: string | null) => {
    setError(null);
    const { error: updateErr } = await supabase.from('schedule_resources').update({
      assigned_user_id: userId, status: userId ? 'occupied' : 'available',
    }).eq('id', resourceId);
    if (updateErr) {
      setError(`Couldn't update chair: ${updateErr.message}`);
      return;
    }
    await fetch();
    onChanged();
  };

  const deleteChair = async () => {
    if (!confirmDelete) return;
    setError(null);
    setDeleting(true);
    const { error: deleteErr } = await supabase.from('schedule_resources').delete().eq('id', confirmDelete.id);
    setDeleting(false);
    if (deleteErr) {
      setError(`Couldn't delete chair: ${deleteErr.message}`);
      return;
    }
    await fetch();
    onChanged();
    setConfirmDelete(null);
  };

  if (!isOpen) return null;

  const assignedIds = new Set(resources.filter(r => r.assigned_user_id).map(r => r.assigned_user_id!));
  const availableOps = operators.filter(o => !assignedIds.has(o.id));

  return (
    <>
      <div onClick={onClose} style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.3)', zIndex: 100 }} />
      <div style={{ position: 'fixed', top: '50%', left: '50%', transform: 'translate(-50%, -50%)', width: 520, maxHeight: '80vh', background: palette.white, borderRadius: 16, boxShadow: shadow.lg, zIndex: 101, display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '16px 20px', borderBottom: `1px solid ${admin.border}` }}>
          <div style={{ fontSize: 17, fontWeight: 700, color: palette.g800 }}>Manage Chairs</div>
          <button onClick={onClose} style={{ background: 'none', border: 'none', cursor: 'pointer', padding: 4 }}><X size={18} color={palette.g500} /></button>
        </div>

        <div style={{ flex: 1, overflowY: 'auto', padding: 20 }}>
          {error && (
            <div style={{
              padding: '10px 12px', borderRadius: 8, marginBottom: 12,
              background: palette.redWash, border: `1px solid ${palette.red}`,
              color: palette.red, fontSize: 13, fontWeight: 500,
            }}>
              {error}
            </div>
          )}
          {resources.map((r) => (
            <div key={r.id} style={{ display: 'flex', alignItems: 'center', gap: 12, padding: '12px 0', borderBottom: `1px solid ${palette.g100}` }}>
              <GripVertical size={16} color={palette.g400} style={{ cursor: 'grab' }} />
              <div style={{ flex: 1 }}>
                <div style={{ fontSize: 14, fontWeight: 600, color: palette.g800 }}>{r.name}</div>
                <div style={{ fontSize: 12, color: palette.g500, marginTop: 2 }}>
                  {r.assigned_user_id ? 'Assigned' : 'Open'}
                  {r.monthly_rent ? ` · $${r.monthly_rent}/mo` : ''}
                </div>
              </div>
              <select
                value={r.assigned_user_id ?? ''}
                onChange={(e) => assignOperator(r.id, e.target.value || null)}
                style={{ fontSize: 12, padding: '4px 8px', borderRadius: 6, border: `1px solid ${admin.border}`, background: palette.white, color: palette.g700, fontFamily: 'inherit' }}
              >
                <option value="">Unassigned</option>
                {r.assigned_user_id && operators.find(o => o.id === r.assigned_user_id) && (
                  <option value={r.assigned_user_id}>{operators.find(o => o.id === r.assigned_user_id)!.first_name} {operators.find(o => o.id === r.assigned_user_id)!.last_name}</option>
                )}
                {availableOps.map(o => (
                  <option key={o.id} value={o.id}>{o.first_name} {o.last_name}</option>
                ))}
              </select>
              <button onClick={() => setConfirmDelete(r)} style={{ background: 'none', border: 'none', cursor: 'pointer', padding: 4 }}>
                <Trash2 size={14} color={palette.g400} />
              </button>
            </div>
          ))}

          {resources.length === 0 && (
            <div style={{ padding: '30px 0', textAlign: 'center', color: palette.g500, fontSize: 13.5 }}>
              No chairs yet. Add one below.
            </div>
          )}

          {/* Add chair */}
          <div style={{ display: 'flex', gap: 8, marginTop: 16 }}>
            <input
              value={newName}
              onChange={(e) => setNewName(e.target.value)}
              placeholder="Chair name (e.g. Chair 1)"
              onKeyDown={(e) => e.key === 'Enter' && addChair()}
              style={{ flex: 1, fontSize: 13, padding: '8px 12px', borderRadius: 8, border: `1px solid ${admin.border}`, fontFamily: 'inherit', color: palette.g800 }}
            />
            <button onClick={addChair} disabled={saving || !newName.trim()} style={{
              display: 'flex', alignItems: 'center', gap: 4, padding: '8px 14px', borderRadius: 8,
              border: 'none', background: palette.accent, fontSize: 13, fontWeight: 600,
              color: palette.white, cursor: 'pointer', fontFamily: 'inherit', opacity: saving ? 0.6 : 1,
            }}><Plus size={14} /> Add</button>
          </div>
        </div>
      </div>

      <ConfirmDialog
        isOpen={!!confirmDelete}
        title={`Delete "${confirmDelete?.name ?? ''}"?`}
        message="Bookings assigned to this chair will need to be reassigned. This cannot be undone."
        confirmLabel="Delete chair"
        confirmVariant="danger"
        loading={deleting}
        onClose={() => { if (!deleting) setConfirmDelete(null); }}
        onConfirm={deleteChair}
      />
    </>
  );
}
